import { useCallback, useEffect, useRef, useState } from 'react';
import { buildSpinFrameImageUrl, selectSpinFrames, toStockNumber } from '|/helpers/vehicle-image';
import useEventHelper from '|/hooks/use-event-helper';
import { getSpinnerData } from '|/services/vex-spinner';

export type VehicleSpinImage =
  | { status: 'pending' }
  | { status: 'unavailable' }
  | { status: 'ready'; url: string; onError: () => void };

const PENDING: VehicleSpinImage = { status: 'pending' };
const UNAVAILABLE: VehicleSpinImage = { status: 'unavailable' };

interface SpinImageOptions {
  /** 2x the CSS box, as everywhere else in the funnel. */
  width: number;
  aspect: number;
  /** When false nothing is requested at all, for a surface or arm that must not use a frame. */
  isEnabled?: boolean;
}

/**
 * For a box `VehicleImage` cannot fill: `hero.jpg` carries no bounding box, so cropping it far
 * from its own aspect clips the roof and the wheels. Only the execution-keyed spin frame has that
 * slack, which is why `unavailable` hands the caller back rather than cropping a stock-keyed URL.
 */
const useVehicleSpinImage = (
  stockNumberInput: number | string | null | undefined,
  { width, aspect, isEnabled = true }: SpinImageOptions
): VehicleSpinImage => {
  const { sendEvent } = useEventHelper();
  const stockNumber = toStockNumber(stockNumberInput);
  const [resolved, setResolved] = useState<{ stockNumber: string; url: string | null } | null>(
    null
  );
  const [hasErrored, setHasErrored] = useState(false);
  const reported = useRef(new Set<string>());

  useEffect(() => {
    if (!isEnabled || !stockNumber) return;

    // The service shares one cached promise across co-mounted surfaces, so nothing may abort it.
    let isCurrent = true;

    void getSpinnerData(stockNumber).then(data => {
      if (!isCurrent) return;

      // Not `[0]`: the list also carries the spin's own `heroImageUrl`, and every frame of a spin
      // that has no `boundingBox` is uncroppable. Both build to `null`, so fall through to the
      // first frame that does crop rather than rendering one that would clip.
      const url = selectSpinFrames(data).reduce<string | null>(
        (found, frame) => found ?? buildSpinFrameImageUrl(frame, { aspect, width }),
        null
      );

      setResolved({ stockNumber, url });
      setHasErrored(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [isEnabled, stockNumber, aspect, width]);

  // A resolution for the previous vehicle must not be read as this one's answer.
  const isResolved = resolved?.stockNumber === stockNumber;
  const url = isResolved ? resolved.url : null;
  const isUnavailable = isResolved && (url === null || hasErrored);

  const onError = useCallback(() => {
    setHasErrored(true);
  }, []);

  useEffect(() => {
    if (!isEnabled || !isUnavailable || !stockNumber) return;

    const reason = hasErrored ? 'spin-frame-errored' : 'spin-frame-missing';
    const reportKey = `${stockNumber}:${reason}`;

    if (reported.current.has(reportKey)) return;

    reported.current.add(reportKey);
    sendEvent('pp_vehicle_image_fallback', { rung: reason, stockNumber });
  }, [isEnabled, isUnavailable, hasErrored, stockNumber, sendEvent]);

  if (!isEnabled || !stockNumber) return UNAVAILABLE;
  if (isUnavailable) return UNAVAILABLE;
  if (!url) return PENDING;

  return { status: 'ready', url, onError };
};

export default useVehicleSpinImage;
